import { useEffect, useRef } from "react";

interface Props {
  onMicrophoneLevel: (level: number) => void;
  onMicrophoneGranted: () => void;
  onMicrophoneDenied: () => void;
  selectedDeviceId?: string;
}

export const useMicrophoneLevels = ({
  onMicrophoneLevel,
  onMicrophoneGranted,
  onMicrophoneDenied,
  selectedDeviceId,
}: Props) => {
  const audioContext = useRef<AudioContext | null>(null);
  const mediaStream = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      stopAudioProcessor();
    };
  }, []);

  useEffect(() => {
    stopAudioProcessor();
    startAudioProcessor();
  }, [selectedDeviceId]);

  const microphoneGranted = async (stream: MediaStream) => {
    onMicrophoneGranted();

    mediaStream.current = stream;
    audioContext.current = new AudioContext();
    await audioContext.current.audioWorklet.addModule(
      process.env.PUBLIC_URL + "/worklet/MicProcessor.js",
    );
    let microphone = audioContext.current.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(audioContext.current, "vumeter");

    node.port.onmessage = (event) => {
      let volume = 0;
      let sensitivity = 5;
      if (event.data.volume) {
        volume = event.data.volume;
      }
      // console.log((volume * 100) / sensitivity);
      onMicrophoneLevel((volume * 100) / sensitivity);
    };

    microphone.connect(node).connect(audioContext.current.destination);
  };

  const microphoneDenied = () => {
    onMicrophoneDenied();
  };

  const startAudioProcessor = async () => {
    // console.log(await navigator.mediaDevices.enumerateDevices());
    // console.log("Trying to start with id: " + selectedDeviceId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: selectedDeviceId },
      });
      await microphoneGranted(stream);
    } catch (ex) {
      console.error(ex);
      microphoneDenied();
    }
  };

  const stopAudioProcessor = () => {
    if (audioContext?.current) {
      audioContext.current.close();
    }
    if (mediaStream?.current) {
      mediaStream.current.getTracks().forEach((track) => track.stop());
    }
    audioContext.current = null;
  };
};
